/**
 * useReleaseTour — the runtime behind every release tour.
 *
 * Handles: eligibility (feature flag + role targeting), "show once" persistence, the
 * auto-start-on-first-eligible-mount behavior, manual replay (for the Help menu), and the 5
 * Mixpanel events Samer's spec calls for (Release Viewed / Tour Started / Tour Completed /
 * Tour Skipped / Feedback Submitted — the last one is exposed as `trackFeedback`, since
 * feedback capture UI is caller-specific, not something this hook renders).
 *
 * Tracking goes through the existing `@/shared/lib/analytics` façade (not `mixpanel-browser`
 * directly) — it already no-ops safely before the app's Mixpanel integration has called
 * `mixpanel.init()`, which a direct `mixpanel.track()` call does not: it throws
 * (`Cannot read properties of undefined (reading 'before_track')`) if init hasn't run yet,
 * which would silently break `start()` before it ever sets `run`.
 *
 * `run` is deliberately a derived value (`manualRun || autoRun`), not state written from an
 * effect — only the one-time 'Release Viewed' analytics call needs an effect, guarded by a ref
 * so it can't double-fire across re-renders.
 *
 * Usage (auto-start once per user, e.g. mounted at the top of the module the tour covers):
 *   const tour = useReleaseTour(CONSENT_MANAGEMENT_TOUR);
 *   <ReleaseTour {...tour} />
 *
 * Usage (manual replay from a Help menu item):
 *   const tour = useReleaseTour(CONSENT_MANAGEMENT_TOUR, { autoStart: false });
 *   <button onClick={tour.start}>Replay: {CONSENT_MANAGEMENT_TOUR.name}</button>
 *   <ReleaseTour {...tour} />
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { STATUS, type EventData } from 'react-joyride';
import { track } from '@/shared/lib/analytics';
import type { ReleaseTourConfig } from './release-tour-types';

const SEEN_KEY_PREFIX = 'scrips-release-tour-seen:';

/**
 * Release tours are parked for now (Ahmed, 19 Oct: hide them, fix later). While this is `true` no tour runs,
 * auto-starts or records analytics, and the Help-menu replay does nothing; `#react-joyride-portal` is also hidden in
 * `showcase-bridge.css`. To bring the tours back, set this to `false` and delete that CSS rule.
 */
export const RELEASE_TOURS_PARKED = true;

function hasSeenTour(tourId: string): boolean {
  try {
    return localStorage.getItem(SEEN_KEY_PREFIX + tourId) === '1';
  } catch {
    // Never let storage access (private browsing, locked-down contexts) crash a tour.
    return false;
  }
}

function markTourSeen(tourId: string): void {
  try {
    localStorage.setItem(SEEN_KEY_PREFIX + tourId, '1');
  } catch {
    /* best-effort only */
  }
}

export interface UseReleaseTourOptions {
  /** Current user's role, for the config's `roles` allow-list. Omit if the tour has no role
   *  targeting. */
  userRole?: string;
  /** Auto-start the tour the first time it's eligible and unseen. Defaults to `true`; pass
   *  `false` for a Help-menu-only replay entry that should never self-trigger. */
  autoStart?: boolean;
}

export interface UseReleaseTourResult {
  /** Pass straight through to `<ReleaseTour run={run} steps={steps} onEvent={onEvent} />`. */
  run: boolean;
  steps: ReleaseTourConfig['steps'];
  onEvent: (data: EventData) => void;
  /** Manually start the tour (Help-menu replay, or a "Show me" button). Fires `Tour Started`. */
  start: () => void;
  /** Whether this browser has already completed/skipped this tour. */
  hasSeen: boolean;
  /** Call after a post-tour feedback prompt is answered. Fires `Feedback Submitted`. */
  trackFeedback: (feedback: { rating?: number; comment?: string }) => void;
}

function isEnabled(config: ReleaseTourConfig): boolean {
  if (config.enabled === false) return false;
  if (typeof config.enabled === 'function') return config.enabled();
  return true;
}

export function useReleaseTour(
  config: ReleaseTourConfig,
  options: UseReleaseTourOptions = {},
): UseReleaseTourResult {
  const { userRole, autoStart = true } = options;
  const [manualRun, setManualRun] = useState(false);
  const [hasSeen, setHasSeen] = useState(() => hasSeenTour(config.id));
  const viewedFiredRef = useRef(false);

  const eligible = useMemo(() => {
    if (RELEASE_TOURS_PARKED) return false;
    if (!isEnabled(config)) return false;
    if (config.roles && userRole && !config.roles.includes(userRole)) return false;
    return true;
  }, [config, userRole]);

  const autoRun = autoStart && eligible && !hasSeen;
  const run = manualRun || autoRun;

  // One-time 'Release Viewed' analytics call when the tour first becomes eligible to
  // auto-show. Guarded by a ref (not `hasSeen`) so it fires exactly once per mount, even if
  // eligibility flips true→false→true (e.g. a role loading in asynchronously).
  useEffect(() => {
    if (!autoRun || viewedFiredRef.current) return;
    viewedFiredRef.current = true;
    track('Release Viewed', { tour: config.id, tourName: config.name });
  }, [autoRun, config.id, config.name]);

  const start = useCallback(() => {
    if (RELEASE_TOURS_PARKED) return;
    track('Tour Started', { tour: config.id, tourName: config.name });
    setManualRun(true);
  }, [config.id, config.name]);

  const onEvent = useCallback(
    (data: EventData) => {
      const { status, index } = data;
      const finished = status === STATUS.FINISHED;
      const skipped = status === STATUS.SKIPPED;
      if (finished || skipped) {
        markTourSeen(config.id);
        setHasSeen(true);
        setManualRun(false);
        track(finished ? 'Tour Completed' : 'Tour Skipped', {
          tour: config.id,
          tourName: config.name,
          ...(skipped ? { atStep: index } : {}),
        });
      }
    },
    [config.id, config.name],
  );

  const trackFeedback = useCallback(
    (feedback: { rating?: number; comment?: string }) => {
      track('Feedback Submitted', {
        tour: config.id,
        tourName: config.name,
        ...feedback,
      });
    },
    [config.id, config.name],
  );

  return { run, steps: config.steps, onEvent, start, hasSeen, trackFeedback };
}
